import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface TenantPlanLimits {
  maxInstructors?: number;
  maxCourses?: number;
  maxLiveClasses?: number;
  maxActiveStudents?: number;
  maxStorageGB?: number;
}

export interface TenantPlanFeatures {
  customDomain?: boolean;
  customPaymentGateway?: boolean;
  whiteLabeling?: boolean;
  analytics?: boolean;
  sso?: boolean;
}

export interface TenantPlan {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  platformFeeRate: number; // e.g. 0.05 = 5%
  price: number;
  currency: string;
  billingCycleMonths: number;
  limits?: TenantPlanLimits;
  features?: TenantPlanFeatures;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface PaginatedResponse<T> {
  docs: T[];
  totalDocs: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

@Injectable({ providedIn: 'root' })
export class TenantPlanApiService {
  private http = inject(HttpClient);
  private base = `${environment.host}/api/v1/tenant-plans`;

  getPublicPlans() {
    return this.http.get<TenantPlan[]>(this.base);
  }

  getAdminPlans(page = 1, limit = 20, isActive?: boolean) {
    let url = `${this.base}/admin?page=${page}&limit=${limit}`;
    if (typeof isActive === 'boolean') {
      url += `&isActive=${isActive}`;
    }
    return this.http.get<PaginatedResponse<TenantPlan>>(url);
  }

  getPlanById(id: string) {
    return this.http.get<TenantPlan>(`${this.base}/admin/${id}`);
  }

  createPlan(dto: Partial<TenantPlan>) {
    return this.http.post<TenantPlan>(`${this.base}/admin`, dto);
  }

  updatePlan(id: string, dto: Partial<TenantPlan>) {
    return this.http.put<TenantPlan>(`${this.base}/admin/${id}`, dto);
  }

  deletePlan(id: string) {
    return this.http.delete<{ message: string }>(`${this.base}/admin/${id}`);
  }

  assignPlanToTenant(tenantId: string, planId: string) {
    return this.http.post(`${this.base}/admin/assign`, { tenantId, planId });
  }

  getWorkspaceUsage() {
    return this.http.get<WorkspaceUsage>(
      `${environment.host}/api/v1/tenants/workspace-usage`,
    );
  }
}

export interface UsageMetric {
  used: number;
  limit: number;
  percent: number;
  unlimited: boolean;
}

export interface WorkspaceUsage {
  plan: {
    name: string;
  };
  limits: {
    maxInstructors: number;
    maxLiveClasses: number;
    maxActiveStudents: number;
    maxStorageGB: number;
  };
  usage: {
    instructors: UsageMetric;
    liveClasses: UsageMetric;
    activeStudents: UsageMetric;
    storage: UsageMetric & {
      usedBytes: number;
      usedGB: number;
      limitGB: number;
    };
  };
}

